// Cadence permissions helper
// Central source of truth for all role-based access control

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  HEAD_OF_BIDS: 'head_of_bids',
  BID_LEAD: 'bid_lead',
  CONTRIBUTOR: 'contributor',
  REVIEWER: 'reviewer',
}

const roleLevel = {
  super_admin: 6,
  admin: 5,
  head_of_bids: 4,
  bid_lead: 3,
  contributor: 2,
  reviewer: 1,
}

// Returns true if the user's role meets or exceeds the required role
export function hasRole(profile, requiredRole) {
  const userLevel = roleLevel[profile?.role] || 0
  const requiredLevel = roleLevel[requiredRole] || 0
  return userLevel >= requiredLevel
}

// Organisation and settings management
export function canManageOrg(profile) {
  return hasRole(profile, ROLES.ADMIN)
}

// Framework and competitor library management
export function canManageLibraries(profile) {
  return hasRole(profile, ROLES.HEAD_OF_BIDS)
}

// Reporting and intelligence
export function canViewReporting(profile) {
  return hasRole(profile, ROLES.HEAD_OF_BIDS)
}

export function canUseOpportunityIntelligence(profile) {
  return hasRole(profile, ROLES.HEAD_OF_BIDS)
}

// Opportunity management
export function canCreateOpportunity(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

export function canEditOpportunity(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

export function canDeleteOpportunity(profile, opportunity) {
  if (!profile || !opportunity) return false
  // Super admin and admin can delete any opportunity
  if (hasRole(profile, ROLES.ADMIN)) return true
  // Head of bids can delete any opportunity in their org
  if (hasRole(profile, ROLES.HEAD_OF_BIDS)) return true
  // Bid lead can only delete their own opportunities
  if (profile.role === ROLES.BID_LEAD) {
    return opportunity.bid_lead_id === profile.id ||
           opportunity.created_by === profile.id
  }
  return false
}

export function canQualify(profile) {
  return hasRole(profile, ROLES.HEAD_OF_BIDS)
}

// Bid plan management
export function canManageBidPlan(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

// Artefact management
export function canManageArtefacts(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

// Action tracker
export function canManageActions(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

export function canUpdateAction(profile, action) {
  if (!profile || !action) return false
  if (hasRole(profile, ROLES.BID_LEAD)) return true
  // Contributors can update actions assigned to them
  if (profile.role === ROLES.CONTRIBUTOR) {
    return action.assigned_to === profile.id
  }
  return false
}

// Question tracker
export function canManageQuestions(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

export function canUpdateQuestion(profile, question) {
  if (!profile || !question) return false
  if (hasRole(profile, ROLES.BID_LEAD)) return true
  if (profile.role === ROLES.CONTRIBUTOR) {
    return question.assigned_to === profile.id
  }
  return false
}

// Clarification questions
export function canManageCQs(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

// Competitor intelligence
export function canManageCompetitors(profile) {
  return hasRole(profile, ROLES.BID_LEAD)
}

// AI features — requires both role and org AI toggle
export function canUseAI(profile) {
  return profile?.organisations?.ai_enabled === true
}

// Settings pages
export function canAccessOrgSettings(profile) {
  return hasRole(profile, ROLES.ADMIN)
}

export function canAccessUserManagement(profile) {
  return hasRole(profile, ROLES.ADMIN)
}

export function canAccessBranding(profile) {
  return hasRole(profile, ROLES.ADMIN)
}

export function canAccessLicence(profile) {
  return hasRole(profile, ROLES.ADMIN)
}

// Read-only check — reviewers get read-only everywhere
export function isReadOnly(profile) {
  return profile?.role === ROLES.REVIEWER
}
